import { FC, PropsWithChildren } from 'react';

import { useAtomValue } from 'jotai';

import { addTag } from '@core';
import { boothIdAtom, userAtom, useSyncAtom } from '@store';

import {
    useBooth,
    useBoothEndpoint,
    useBoothEvents,
    useBoothInfo,
    useBoothSessionManager,
    useBoothSessionRefresh,
    useUser,
    useUserEvents,
} from './hooks';
import { BoothLoginScreen, FloorLoginScreen } from './screens';
import { LoadingScreen } from './screens/LoadingScreen/LoadingScreen';

export const FloorProvider: FC<PropsWithChildren> = ({ children }) => {
    const { data: booth, isLoading } = useBooth();
    const { data: user } = useUser();
    const { data: boothInfo } = useBoothInfo();

    addTag('boothUserId', booth?.id ?? 'None');
    addTag('boothId', boothInfo?.id ?? 'None');
    addTag('beastEndpoint', boothInfo?.beastEndpoint ?? 'None');
    useSyncAtom(boothIdAtom, boothInfo?.id, true);

    useBoothSessionManager();
    useBoothEvents();
    useUserEvents();
    useBoothSessionRefresh({ listenForForceRefresh: true });
    useBoothEndpoint();

    useSyncAtom(userAtom, user, true);
    const userAtomValue = useAtomValue(userAtom);

    if (isLoading) {
        return <LoadingScreen typographyVariant="h2" message="Loading application..." />;
    }

    if (!booth) {
        return <BoothLoginScreen />;
    }

    if (!user) {
        return <FloorLoginScreen />;
    }

    /**
     * Temporary guard to prevent rendering until user state is fully synced.
     * Without this, userAtom is undefined on first render (sync happens in an effect),
     *
     * TODO: Replace with a proper initialization flow once designed.
     */
    if (!boothInfo || !userAtomValue) {
        return <LoadingScreen message="Loading application..." />;
    }
    return children;
};
